import React, { useEffect, useRef, useState, useImperativeHandle, useCallback } from "react";
import Map from "ol/Map.js";
import View from "ol/View.js";
import TileLayer from "ol/layer/Tile.js";
import TileWMS from 'ol/source/TileWMS';
import OSM from "ol/source/OSM";
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Draw, Modify, Snap } from "ol/interaction";
import GeoJSON from 'ol/format/GeoJSON';
import WKT from 'ol/format/WKT';
import Collection from 'ol/Collection';
import Point from 'ol/geom/Point';
import MultiPoint from 'ol/geom/MultiPoint';
import { defaults as defaultControls, Zoom } from 'ol/control';
import { Style, Fill, Stroke, Circle, Text } from 'ol/style';
import Feature from 'ol/Feature';
import { useGeographic, transform, getPointResolution } from 'ol/proj';
import XYZ from 'ol/source/XYZ';
import "ol/ol.css";
import { useRedContext } from "../context/RedContext";

// Iconos inline SVG
const ToolsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
);

const LayersIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 2 7 12 12 22 7 12 2"/>
        <polyline points="2 17 12 22 22 17"/>
        <polyline points="2 12 12 17 22 12"/>
    </svg>
);

const DrawingsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 19l7-7 3 3-7 7-3-3z"/>
        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
        <path d="M2 2l7.586 7.586"/>
        <circle cx="11" cy="11" r="2"/>
    </svg>
);

// --- ESTILOS ---
const STYLES = {
    SEDE: new Style({
        image: new Circle({
            radius: 8,
            fill: new Fill({ color: '#e36212' }),
            stroke: new Stroke({ color: '#FFFFFF', width: 2 })
        })
    }),
    SEDE_SELECTED: new Style({
        image: new Circle({
            radius: 12,
            fill: new Fill({ color: '#ff6600' }),
            stroke: new Stroke({ color: '#FFFFFF', width: 3 })
        })
    }),
    PARCELA: new Style({
        fill: new Fill({ color: 'rgba(0, 153, 255, 0.2)' }),
        stroke: new Stroke({ color: '#0099FF', width: 2.5 })
    }),
    PARCELA_SELECTED: new Style({
        fill: new Fill({ color: 'rgba(255, 102, 0, 0.3)' }),
        stroke: new Stroke({ color: '#ff6600', width: 4 })
    }),
    PARCELA_LABEL: (text, feature) => {
        const geometry = feature.getGeometry();
        const extent = geometry.getExtent();
        const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
        return new Style({
            geometry: new Point(center),
            text: new Text({
                text: text,
                font: 'bold 14px Arial, sans-serif',
                fill: new Fill({ color: '#000000' }),
                stroke: new Stroke({ color: '#FFFFFF', width: 4 }),
                offsetY: 0,
                textAlign: 'center',
                textBaseline: 'middle',
                placement: 'point'
            }),
            zIndex: 100
        });
    },
    DRAWING_PREVIEW: new Style({
        fill: new Fill({ color: 'rgba(0, 153, 255, 0.1)' }),
        stroke: new Stroke({ color: '#0099FF', width: 2, lineDash: [5, 5] }),
        image: new Circle({ radius: 5, stroke: new Stroke({ color: '#0099FF', width: 2 }), fill: new Fill({ color: '#ffffff' }) })
    }),
    VERTEX_DOT: new Style({
        image: new Circle({
            radius: 5,
            fill: new Fill({ color: '#ffffff' }),
            stroke: new Stroke({ color: '#0099FF', width: 2 })
        })
    }),
    SNAP_GUIDE: new Style({
        image: new Circle({
            radius: 10,
            fill: new Fill({ color: 'rgba(227, 98, 18, 0.4)' }),
            stroke: new Stroke({ color: '#e36212', width: 2 })
        })
    })
};

const MapViewMapeo = React.forwardRef(({ partido, onFeatureDataChange, initialFeatures, initialFeaturesParcelas, initialParcelasMetadata, readOnly = false, sinSede = false }, ref) => {
    useGeographic();
    const { formData, updateSection } = useRedContext();
    
    const mapRef = useRef(null);
    const zoomRef = useRef(null); // Ref para el contenedor del zoom
    const mapInstance = useRef(null);
    const parcelasLayerRef = useRef(null);
    const arbaLayerRef = useRef(null);
    const baseLayerRef = useRef(null); // Ref para la capa base actual

	const editModifyInteractionRef = useRef(null); // Ref para la interacción Modify de edición
	const modifyInteractionRef = useRef(null);
    
    // Sources persistentes (No cambian entre renders)
    const sedeSource = useRef(new VectorSource());
    const parcelasSource = useRef(new VectorSource());
    const snapOverlaySource = useRef(new VectorSource());
    const prevSinSede = useRef(sinSede);

	const [geoJsonLayer, setGeoJsonLayer] = useState(null); // Capa del municipio
	const [selectedWKTIndex, setSelectedWKTIndex] = useState(null);
    const [parcelaData, setParcelaData] = useState(() => {
        // Inicializar desde los metadatos si existen
        if (initialParcelasMetadata && Array.isArray(initialParcelasMetadata)) {
            const initialData = {};
            initialParcelasMetadata.forEach((meta, index) => {
                initialData[index] = {
                    partida: meta.partida || '',
                    parcela: meta.parcela || ''
                };
            });
            console.log('Inicializando parcelaData desde metadatos:', initialData);
            return initialData;
        }
        return {};
    });
    const selectedFeatureRef = useRef(null);

    const [isEditingMode, setIsEditingMode] = useState(false);
    const [activeTool, setActiveTool] = useState(null);
    const [hasGeometries, setHasGeometries] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [showArbaForm, setShowArbaForm] = useState(false);
    const [showArbaLayer, setShowArbaLayer] = useState(false);
    const [arbaInfoMode, setArbaInfoMode] = useState(false);
    const [arbaFeatureInfo, setArbaFeatureInfo] = useState(null);
    const [arbaInfoPosition, setArbaInfoPosition] = useState(null);
    const [arbaPartidas, setArbaPartidas] = useState('');
    const [isLoadingArba, setIsLoadingArba] = useState(false);
    const [arbaLoadingInfo, setArbaLoadingInfo] = useState(false);
    const [arbaError, setArbaError] = useState(null);

    // Estado para secciones del acordeón
    const [accordionTools, setAccordionTools] = useState(true);
    const [accordionLayers, setAccordionLayers] = useState(false);

    // Estado para capa base seleccionada
    const [baseLayer, setBaseLayer] = useState('osm'); // 'osm' | 'ign' | 'positron' | 'dark'

    const toggleMinimize = () => setIsMinimized(prev => !prev);

    const notifyGeometryChange = useCallback(() => {
        const format = new GeoJSON();
        const pointFeature = sedeSource.current.getFeatures()[0];
        const point = pointFeature ? pointFeature.getGeometry().getCoordinates() : null;
        
        // Crear array de parcelas con geometría y datos - leer directamente de las features
        const parcelasWithData = parcelasSource.current.getFeatures().map((f, index) => {
            const geom = f.getGeometry();
            // Leer datos directamente de la feature (que ya tiene los valores actualizados)
            const partida = f.get('partida') || '';
            const parcela = f.get('parcela') || '';
            return {
                geometry: {
                    type: 'MultiPolygon',
                    coordinates: geom.getType() === 'Polygon' ? [geom.getCoordinates()] : geom.getCoordinates()
                },
                partida: partida,
                parcela: parcela
            };
        });
        
        const polygons = parcelasWithData.map(p => p.geometry);

        onFeatureDataChange({ 
            geom_sede: point, 
            geom_parcelas: polygons,
            parcelas_data: parcelasWithData,
            parcelas_metadata: parcelasWithData.map(p => ({ 
                partida: p.partida, 
                parcela: p.parcela 
            }))
        });
        
        updateSection('identidad', {
            ...formData.identidad,
            geom_sede: pointFeature ? format.writeGeometry(pointFeature.getGeometry()) : null,
            geom_parcelas: parcelasSource.current.getFeatures().map(f => format.writeGeometry(f.getGeometry())),
            parcelas_metadata: parcelasWithData.map(p => ({ 
                partida: p.partida, 
                parcela: p.parcela 
            }))
        });
    }, [onFeatureDataChange, updateSection, formData.identidad]);

    // Función para actualizar datos de parcela
    const updateParcelaData = useCallback((index, field, value) => {
        // Calcular el nuevo estado
        const newData = {
            ...parcelaData[index],
            [field]: value
        };
        
        setParcelaData(prev => ({
            ...prev,
            [index]: newData
        }));
        
        // Construir label con los datos actualizados
        const partida = newData.partida || '';
        const parcela = newData.parcela || '';
        let label = '';
        if (parcela) label = parcela;
        else if (partida) label = `PDA ${partida}`;
        else label = `Parcela ${index + 1}`;
        
        // Actualizar la feature con los datos
        const features = parcelasSource.current.getFeatures();
        console.log(`Updating parcela ${index}:`, { field, value, label, totalFeatures: features.length });
        if (features[index]) {
            features[index].set(field, value);
            features[index].set('label', label);
            console.log(`Feature ${index} label set to:`, label);
            // Forzar redibujado de la capa
            if (parcelasLayerRef.current) {
                parcelasLayerRef.current.changed();
                console.log('Layer changed() called');
            }
            parcelasSource.current.changed();
        }
        notifyGeometryChange();
    }, [notifyGeometryChange, parcelaData]);

    const stopInteractions = useCallback(() => {
        const map = mapInstance.current;
        if (!map) return;

        map.getInteractions().getArray()
            .filter(i => i instanceof Draw || i instanceof Modify || i instanceof Snap)
            .forEach(i => map.removeInteraction(i));

        snapOverlaySource.current.clear();
        setActiveTool(null);
        setIsEditingMode(false);
        map.getTargetElement().style.cursor = '';
        if (parcelasLayerRef.current) parcelasLayerRef.current.changed();
    }, []);

    // --- EFECTO: ESCUCHAR TECLA ESC PARA CANCELAR DIBUJO ---
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && activeTool && activeTool !== 'edit') {
                e.preventDefault();
                e.stopPropagation();
                stopInteractions();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeTool, stopInteractions]);

    // --- ZOOM A GEOMETRÍAS ---
    const zoomToGeometries = useCallback(() => {
        const map = mapInstance.current;
        if (!map) return;

        const parcelasFeatures = parcelasSource.current.getFeatures();
        const sedeFeatures = sedeSource.current.getFeatures();

        // Si hay parcelas, hacer zoom a la extensión de las parcelas
        if (parcelasFeatures.length > 0) {
            const extent = parcelasSource.current.getExtent();
            map.getView().fit(extent, {
                padding: [50, 50, 50, 50],
                duration: 800,
                maxZoom: 19
            });
        }
        // Si no hay parcelas pero hay sede, hacer zoom al punto
        else if (sedeFeatures.length > 0) {
            const coordinates = sedeFeatures[0].getGeometry().getCoordinates();
            map.getView().animate({
                center: coordinates,
                zoom: 17,
                duration: 800
            });
        }
    }, []);

    // Limpiar sede cuando sinSede se activa
    useEffect(() => {
        if (sinSede) {
            sedeSource.current.clear();
            notifyGeometryChange();
        }
        prevSinSede.current = sinSede;
    }, [sinSede, notifyGeometryChange]);

    // --- DIBUJO ---
    const startDrawing = useCallback((type) => {
        if (readOnly || !mapInstance.current) return;
        if (sinSede && type === 'point') return;
        stopInteractions();
        // Desactivar modo info ARBA para evitar conflictos al dibujar
        setArbaInfoMode(false);
        
        const map = mapInstance.current;
        const draw = new Draw({
            source: type === 'point' ? sedeSource.current : parcelasSource.current,
            type: type === 'point' ? 'Point' : 'Polygon',
            style: STYLES.DRAWING_PREVIEW 
        });

        draw.on('drawend', () => {
            if (type === 'point') sedeSource.current.clear();
            setTimeout(() => {
                notifyGeometryChange();
                stopInteractions();
            }, 100);
        });

        map.addInteraction(draw);
        map.addInteraction(new Snap({ source: parcelasSource.current, pixelTolerance: 15 }));
        setActiveTool(type);
    }, [readOnly, sinSede, stopInteractions, notifyGeometryChange]);

	

    // --- EDICIÓN ---
    /*const toggleEditMode = useCallback(() => {
        const map = mapInstance.current;
        if (!map) return;
        
        if (isEditingMode) {
			// Desactivar modo edición
			if (editModifyInteractionRef.current) {
				map.removeInteraction(editModifyInteractionRef.current);
				editModifyInteractionRef.current = null;
			}
			setIsEditingMode(false);

            //stopInteractions();
        } else {
            //stopInteractions();
            const allFeatures = [...sedeSource.current.getFeatures(), ...parcelasSource.current.getFeatures()];
            if (allFeatures.length === 0) return;

            const modify = new Modify({
                features: new Collection(allFeatures),
                pixelTolerance: 20
            });
            modify.on('modifyend', notifyGeometryChange);
            
            map.addInteraction(modify);
			editModifyInteractionRef.current = modify;
            // NOTA: No agregamos Snap en modo edición porque interfiere con Modify
            // El usuario puede editar vértices libremente y los cambios se guardan
			//map.addInteraction(new Snap({ source: parcelasSource.current, pixelTolerance: 30 }));
            
            setIsEditingMode(true);
            setActiveTool('edit');
            if (parcelasLayerRef.current) parcelasLayerRef.current.changed();
        }
    }, [isEditingMode, stopInteractions, notifyGeometryChange]);
	*/
	// --- MODO EDICIÓN DE GEOMETRÍAS EXISTENTES ---
	/*
	const toggleEditMode = () => {
		const map = mapInstance.current;
		if (!map) return;

		if (isEditingMode) {
			// Desactivar modo edición
			if (editModifyInteractionRef.current) {
				map.removeInteraction(editModifyInteractionRef.current);
				editModifyInteractionRef.current = null;
			}
			setIsEditingMode(false);
		} else {
			// Activar modo edición - crear Modify para sede y parcelas
			const editSources = [];
			if (sedeSource.current.getFeatures().length > 0) {
				editSources.push(sedeSource.current);
			}
			if (parcelasSource.current.getFeatures().length > 0) {
				editSources.push(parcelasSource.current);
			}

			if (editSources.length === 0) {
				alert("No hay geometrías para editar. Dibuje primero una sede o parcela.");
				return;
			}

			// Detener dibujo si está activo
			//StopDrawing();

			// Crear colección de features de ambas fuentes
			const allFeatures = new Collection([
				...sedeSource.current.getFeatures(),
				...parcelasSource.current.getFeatures()
			]);

			// Crear Modify que permita editar todas las features
			// Usar estilo con resaltado naranja para los vértices
			const editModify = new Modify({
				features: allFeatures,
				style: new Style({
					image: new Circle({
						radius: 8,
						fill: new Fill({ color: 'rgba(255, 255, 255, 0.8)' }),
						stroke: new Stroke({ color: '#ff6600', width: 3 })
					}),
					stroke: new Stroke({
						color: '#ff6600',
						width: 2,
						lineDash: [5, 5]
					})
				})
			});
			editModify.on('modifyend', () => {
				console.log("Geometría modificada");
				notifyGeometryChange();
			});
			map.addInteraction(editModify);
			editModifyInteractionRef.current = editModify;

			// Agregar snap para editar
			//addSnapInteraction();

			setIsEditingMode(true);
		}
	};
	*/
	const toggleEditMode = useCallback(() => {
		const map = mapInstance.current;
		if (!map) return;

		if (isEditingMode) {
			// Desactivar modo edición
			if (editModifyInteractionRef.current) {
				map.removeInteraction(editModifyInteractionRef.current);
				editModifyInteractionRef.current.dispose(); // IMPORTANTE: liberar recursos
				editModifyInteractionRef.current = null;
			}
			/*if (snapInteractionRef.current) {
				map.removeInteraction(snapInteractionRef.current);
				snapInteractionRef.current = null;
			}*/
			setIsEditingMode(false);
		} else {
			// Verificar si hay geometrías para editar
			const sedeFeatures = sedeSource.current.getFeatures();
			const parcelasFeatures = parcelasSource.current.getFeatures();
			const allFeatures = [...sedeFeatures, ...parcelasFeatures];

			if (allFeatures.length === 0) {
				alert("No hay geometrías para editar. Dibuje primero una sede o parcela.");
				return;
			}

			// DETENER cualquier interacción activa
			//stopDrawing();

			// Crear Modify apuntando directamente a los sources (mejor que Collection)
			const modify = new Modify({
				source: parcelasSource.current,
				pixelTolerance: 15,
				// Permitir eliminar vértices con Alt + Click
				deleteCondition: (event) => {
					return event.originalEvent.altKey;
				}
			});

			modify.on('modifystart', () => {
				map.getTargetElement().style.cursor = 'grabbing';
			});

			modify.on('modifyend', () => {
				map.getTargetElement().style.cursor = '';
				notifyGeometryChange();
			});

			map.addInteraction(modify);
			editModifyInteractionRef.current = modify;

			// Agregar Snap SOLO para vértices (no bordes) con tolerancia menor que Modify
			// Esto permite alinear vértices sin interferir con la edición
			const editSnap = new Snap({
				source: parcelasSource.current,
				pixelTolerance: 10, // Menor que Modify (15) para no interferir
				vertex: true,
				edge: false // Solo vértices, no bordes
			});
			map.addInteraction(editSnap);

			setIsEditingMode(true);

			// Feedback visual
			console.log("Modo edición activado - Puede arrastrar vértices");
		}
	}, [isEditingMode, notifyGeometryChange]);//stopDrawing

	// --- IMPORTAR DESDE ARBA ---
	const importFromArba = useCallback(async () => {
		if (!partido || !arbaPartidas.trim()) {
			alert('Por favor, ingrese al menos un número de partida');
			return;
		}

		// Parsear partidas (separadas por comas o guiones)
		const partidas = arbaPartidas
			.split(/[,\-]+/)
			.map(p => p.trim())
			.filter(p => p.length > 0);

		if (partidas.length === 0) {
			alert('No se encontraron números de partida válidos');
			return;
		}

		setIsLoadingArba(true);
		const wktFormat = new WKT();
		let importedCount = 0;
		let failedPartidas = [];

		for (const partida of partidas) {
			try {
				console.log(`Consultando ARBA: partido=${partido}, partida=${partida}`);
				// ARBA espera el partido sin ceros a la izquierda (009 -> 9, 012 -> 12)
				const partidoNumero = parseInt(partido, 10);
				// Usar proxy del backend para evitar CORS
				const response = await fetch(
					`/api/arba/partida?pdo=${encodeURIComponent(partidoNumero)}&pda=${encodeURIComponent(partida)}`
				);
				const data = await response.json();

				if (data.found && data.result) {
					// Parsear WKT a geometría OpenLayers
					const geometry = wktFormat.readGeometry(data.result);
					
					// Transformar de EPSG:3857 a EPSG:4326 (geográfico)
					geometry.transform('EPSG:3857', 'EPSG:4326');

					// Crear feature
					const feature = new Feature({ geometry });
					feature.set('partida', partida);
					feature.set('parcela', ''); // ARBA no devuelve parcela
					feature.set('label', `PDA ${partida}`);
					feature.set('source', 'arba');

					parcelasSource.current.addFeature(feature);
					importedCount++;
					console.log(`Partida ${partida} importada correctamente`);
				} else {
					console.warn(`Partida ${partida} no encontrada en ARBA`);
					failedPartidas.push(partida);
				}
			} catch (error) {
				console.error(`Error importando partida ${partida}:`, error);
				failedPartidas.push(partida);
			}
		}

		setIsLoadingArba(false);
		setShowArbaForm(false);
		setArbaPartidas('');

		// Notificar cambios
		if (importedCount > 0) {
			notifyGeometryChange();
			// Hacer zoom a las nuevas parcelas
			const extent = parcelasSource.current.getExtent();
			if (extent && extent[0] !== Infinity) {
				mapInstance.current?.getView().fit(extent, {
					padding: [50, 50, 50, 50],
					duration: 800,
					maxZoom: 19
				});
			}
		}

		// Mostrar resumen
		if (failedPartidas.length > 0) {
			alert(`Importadas: ${importedCount} parcelas.\nNo encontradas: ${failedPartidas.join(', ')}`);
		} else if (importedCount > 0) {
			alert(`Se importaron ${importedCount} parcelas correctamente`);
		} else {
			alert('No se pudo importar ninguna parcela');
		}
	}, [partido, arbaPartidas, notifyGeometryChange]);

    // --- 1. EFECTO: INICIALIZACIÓN ÚNICA DEL MAPA ---
    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return;

        const pLayer = new VectorLayer({ 
            source: parcelasSource.current, 
            zIndex: 5,
            style: (f, resolution) => {
                const isSelected = f.get('selected');
                const label = f.get('label');
                const partida = f.get('partida');
                const parcela = f.get('parcela');
                const id = f.getId();
                const styles = [isSelected ? STYLES.PARCELA_SELECTED : STYLES.PARCELA];
                
                // Agregar label solo si existe y el zoom es cercano
                // Para EPSG:4326 (grados), 0.0001 ≈ zoom 16 (cerca), 0.001 ≈ zoom 14 (lejano)
                if (label && resolution < 0.0001) {
                    styles.push(STYLES.PARCELA_LABEL(label, f));
                }
                
                // Verificamos el estado de edición guardado en el mapa
                if (mapInstance.current?.get('isEditing')) {
                    styles.push(new Style({
                        image: STYLES.VERTEX_DOT.getImage(),
                        geometry: (feat) => {
                            const g = feat.getGeometry();
                            return g.getType() === 'Polygon' ? new MultiPoint(g.getCoordinates()[0]) : g;
                        }
                    }));
                }
                return styles;
            }
        });
        parcelasLayerRef.current = pLayer;

        // Crear capa ARBA Parcelario
        const arbaWmsLayer = new TileLayer({
            source: new TileWMS({
                url: 'https://geo.arba.gov.ar/geoserver/idera/wms',
                params: {
                    'LAYERS': 'idera:Parcela',
                    'TILED': true,
                    'FORMAT': 'image/png',
                    'TRANSPARENT': true,
                    'VERSION': '1.1.1',
                    'SRS': 'EPSG:3857'
                },
                attributions: '© ARBA',
                crossOrigin: 'anonymous'
            }),
            visible: false,
            zIndex: 15,
            opacity: 0.8
        });
        arbaLayerRef.current = arbaWmsLayer;

        // Crear capa base inicial (OSM)
        const osmLayer = new TileLayer({ source: new OSM() });
        baseLayerRef.current = osmLayer;

        const initialMap = new Map({
            target: mapRef.current,
            layers: [
                osmLayer, // Capa base (se cambiará dinámicamente)
                arbaWmsLayer,
                pLayer,
                new VectorLayer({ 
                    source: sedeSource.current, 
                    style: (f) => f.get('selected') ? STYLES.SEDE_SELECTED : STYLES.SEDE, 
                    zIndex: 10 
                }),
                new VectorLayer({ source: snapOverlaySource.current, style: STYLES.SNAP_GUIDE, zIndex: 99 })
            ],
            view: new View({
                center: [-60.50, -36.67],
                zoom: 7,
                minZoom: 6,
                maxZoom: 18,
                extent: [-65, -42, -55, -33],
                constrainOnlyCenter: true,
                smoothExtentConstraint: true
            }),
            controls: defaultControls({ attribution: false })            
        });

        mapInstance.current = initialMap;
        
        return () => {
            initialMap.setTarget(undefined);
            mapInstance.current = null;
        };
    }, []);

	// --- EFECTO: ZOOM AL MUNICIPIO --- //RED: no borrar!! importante para zoom al partido:
	useEffect(() => {
		if (!mapInstance.current || !partido) return;

		const fetchPartidoGeom = async () => {
			try {
				const response = await fetch(`/api/partido_geom/${partido}`);
				const geojsonData = await response.json();

				if (geojsonData?.features?.length > 0) {
					const sourcePartido = new VectorSource({
						features: new GeoJSON().readFeatures(geojsonData)
					});

					const layerPartido = new VectorLayer({
						source: sourcePartido,
						style: new Style({
							fill: new Fill({ color: 'rgba(255, 0, 0, 0.1)' }),
							stroke: new Stroke({ color: 'red', width: 2, lineDash: [4, 8] }),
						}),
					});

					if (geoJsonLayer) mapInstance.current.removeLayer(geoJsonLayer);
					mapInstance.current.addLayer(layerPartido);
					setGeoJsonLayer(layerPartido);

					mapInstance.current.getView().fit(sourcePartido.getExtent(), {
						padding: [50, 50, 50, 50],
						duration: 1000,
					});
				}
			} catch (error) {
				console.error('Error fetching municipio geom:', error);
			}
		};

		fetchPartidoGeom();
	}, [partido]);

    // --- 2. EFECTO: SINCRONIZAR DATOS INICIALES (Crucial para que aparezcan las capas) ---
    useEffect(() => {
        if (!mapInstance.current) return;

		// Parsear initialFeatures si viene como string (desde localStorage)
		let parsedInitialFeatures = initialFeatures;
		if (typeof initialFeatures === 'string') {
			try {
				parsedInitialFeatures = JSON.parse(initialFeatures);
			} catch (e) {
				console.error('Error parseando initialFeatures:', e);
				parsedInitialFeatures = null;
			}
		}

		const hasSede = parsedInitialFeatures && (parsedInitialFeatures.type === 'Feature' || parsedInitialFeatures.type === 'FeatureCollection' || parsedInitialFeatures.type === 'Point' || Array.isArray(parsedInitialFeatures.coordinates));
		const hasParcelas = initialFeaturesParcelas && Array.isArray(initialFeaturesParcelas) && initialFeaturesParcelas.length > 0;

		console.log("Has sede:", hasSede, "Has parcelas:", hasParcelas);
		console.log("initialFeatures:", parsedInitialFeatures);
		console.log("initialFeaturesParcelas:", initialFeaturesParcelas);

		if (!hasSede && !hasParcelas) {
			console.log("No hay geometrías para cargar");
			return;
		}

		try {
			//vectorSource.current.clear();
			sedeSource.current.clear();
			parcelasSource.current.clear();

			const format = new GeoJSON();

			// Cargar sede (solo si no tiene sinSede)
			if (hasSede && !sinSede) {
				console.log("Cargando sede:", parsedInitialFeatures);
				const features = format.readFeatures(parsedInitialFeatures);
				console.log("Features de sede parseadas:", features.length);
				if (features.length > 0) {
					sedeSource.current.addFeatures(features);
				}
			}

			// Cargar parcelas (array de geometrías GeoJSON o Features)
			if (hasParcelas) {
				console.log("Cargando parcelas:", initialFeaturesParcelas.length);
				console.log("Metadatos disponibles:", initialParcelasMetadata);
				initialFeaturesParcelas.forEach((item, index) => {
					try {
						console.log(`Parcela ${index}:`, item);
						
						// Extraer geometría y metadatos del nuevo formato del backend
						// Formato: {geometry: GeoJSON, partida: string, parcela: string}
						let geometryData;
						let partida = '';
						let parcela = '';
						
						if (item && item.geometry) {
							// Nuevo formato con metadatos incluidos
							geometryData = item.geometry;
							partida = item.partida || '';
							parcela = item.parcela || '';
						} else if (item && item.type === 'Feature') {
							// Formato Feature GeoJSON estándar
							geometryData = item.geometry;
						} else {
							// Formato antiguo: solo geometría
							geometryData = item;
						}
						
						const geometry = format.readGeometry(geometryData);
						console.log(`Geometría ${index} parseada:`, geometry.getType());
						const feature = new Feature({ geometry });
						
						// Usar metadatos del backend o de initialParcelasMetadata (fallback)
						const metadata = initialParcelasMetadata?.[index] || {};
						partida = partida || metadata.partida || '';
						parcela = parcela || metadata.parcela || '';
						
						feature.set('partida', partida);
						feature.set('parcela', parcela);
						
						// Construir y asignar label
						let label = '';
						if (parcela) label = parcela;
						else if (partida) label = `PDA ${partida}`;
						else label = `Parcela ${index + 1}`;
						feature.set('label', label);
						
						console.log(`Feature ${index} creada con:`, { partida, parcela, label });
						parcelasSource.current.addFeature(feature);
					} catch (error) {
						console.error('Error cargando parcela:', error, item);
					}
				});
				console.log("Total parcelas en source:", parcelasSource.current.getFeatures().length);
			}
		} catch (e) {
			console.error("Error cargando features iniciales:", e);
		}
        
        /*const format = new GeoJSON();
        sedeSource.current.clear();
        parcelasSource.current.clear();

        if (initialFeatures) {
            sedeSource.current.addFeatures(format.readFeatures(initialFeatures));
        }
        
        if (initialFeaturesParcelas && initialFeaturesParcelas.length > 0) {
            initialFeaturesParcelas.forEach(p => {
                const geom = typeof p === 'string' ? format.readGeometry(p) : p;
                parcelasSource.current.addFeature(new Feature({ geometry: geom }));
            });
        }
        
        // Si hay datos, intentamos centrar el mapa solo la primera vez que cargan
        const allFeatures = [...sedeSource.current.getFeatures(), ...parcelasSource.current.getFeatures()];
        if (allFeatures.length > 0) {
            const extent = parcelasSource.current.getExtent();
            //mapInstance.current.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 500 });
        }
		*/

    }, [initialFeatures, initialFeaturesParcelas, initialParcelasMetadata]); // Se dispara cuando llegan los datos

    // --- 3. EFECTO: ACTUALIZAR ESTADO DE EDICIÓN ---
    useEffect(() => {
        if (mapInstance.current) {
            mapInstance.current.set('isEditing', isEditingMode);
            parcelasLayerRef.current.changed();
        }
    }, [isEditingMode]);

    // --- EFECTO: CAMBIAR CAPA BASE ---
    useEffect(() => {
        const map = mapInstance.current;
        if (!map || !baseLayerRef.current) return;

        // Remover capa base anterior
        map.removeLayer(baseLayerRef.current);

        // Crear nueva capa base según selección
        let newBaseLayer;
        switch (baseLayer) {
            case 'ign':
                newBaseLayer = new TileLayer({
                    source: new XYZ({
                        url: 'https://wms.ign.gob.ar/geoserver/gwc/service/tms/1.0.0/capabaseargenmap@EPSG%3A3857@png/{z}/{x}/{-y}.png',
                        attributions: '© Instituto Geográfico Nacional'
                    })
                });
                break;
            case 'positron':
                newBaseLayer = new TileLayer({
                    source: new XYZ({
                        url: 'https://{a-d}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
                        attributions: '© CARTO'
                    })
                });
                break;
            case 'dark':
                newBaseLayer = new TileLayer({
                    source: new XYZ({
                        url: 'https://{a-d}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
                        attributions: '© CARTO'
                    })
                });
                break;
            case 'voyager':
                newBaseLayer = new TileLayer({
                    source: new XYZ({
                        url: 'https://{a-d}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
                        attributions: '© CARTO'
                    })
                });
                break;
            case 'satellite':
                newBaseLayer = new TileLayer({
                    source: new XYZ({
                        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                        attributions: 'Tiles © Esri'
                    })
                });
                break;
            default: // osm
                newBaseLayer = new TileLayer({ source: new OSM() });
        }

        // Insertar al principio (índice 0)
        map.getLayers().insertAt(0, newBaseLayer);
        baseLayerRef.current = newBaseLayer;
    }, [baseLayer]);

    // --- EFECTO: TOGGLE CAPA ARBA ---
    useEffect(() => {
        if (arbaLayerRef.current) {
            arbaLayerRef.current.setVisible(showArbaLayer);
        }
    }, [showArbaLayer]);

    // --- EFECTO: GETFEATUREINFO PARA CAPA ARBA ---
    useEffect(() => {
        const map = mapInstance.current;
        if (!map || !showArbaLayer || !arbaInfoMode) {
            // Limpiar popup si la capa se oculta o modo info se desactiva
            setArbaFeatureInfo(null);
            setArbaError(null);
            return;
        }

        const handleClick = async (evt) => {
            if (arbaLoadingInfo) return;

            setArbaLoadingInfo(true);
            setArbaError(null);
            setArbaFeatureInfo(null);

            const viewResolution = map.getView().getResolution();
            const source = arbaLayerRef.current?.getSource();
            if (!source) {
                setArbaLoadingInfo(false);
                return;
            }

            // La capa ARBA usa SRS:EPSG:3857. Transformar coordenada y resolución a 3857
            // para que getFeatureInfoUrl calcule correctamente el tile y BBOX
            const coord3857 = transform(evt.coordinate, 'EPSG:4326', 'EPSG:3857');
            const res3857 = getPointResolution('EPSG:4326', viewResolution, evt.coordinate);
            const url = source.getFeatureInfoUrl(
                coord3857,
                res3857,
                'EPSG:3857',
                {
                    'INFO_FORMAT': 'application/json',
                    'QUERY_LAYERS': 'idera:Parcela'
                }
            );

            if (url) {
                try {
                    const response = await fetch(url);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const data = await response.json();
                    if (data.features && data.features.length > 0) {
                        setArbaFeatureInfo(data.features[0]);
                        setArbaInfoPosition({
                            x: evt.pixel[0],
                            y: evt.pixel[1]
                        });
                    } else {
                        setArbaError('No se encontraron parcelas en esta ubicación');
                    }
                } catch (error) {
                    console.error('Error GetFeatureInfo:', error);
                    setArbaError('Error al consultar ARBA. Intente nuevamente más tarde.');
                }
            } else {
                setArbaError('No se pudo generar la consulta ARBA');
            }

            setArbaLoadingInfo(false);
        };

        map.on('singleclick', handleClick);
        return () => {
            map.un('singleclick', handleClick);
        };
    }, [showArbaLayer, arbaInfoMode]);

    // --- IMPORTAR PARCELA DESDE POPUP ARBA ---
    const importArbaParcelFromPopup = useCallback(async () => {
        console.log('=== importArbaParcelFromPopup INICIADO ===');
        if (!arbaFeatureInfo || !arbaFeatureInfo.properties) {
            alert('No hay datos de propiedades disponibles');
            return;
        }

        const properties = arbaFeatureInfo.properties;
        const pda = properties.pda || '';
        const cca = properties.cca || '';

        if (!pda || !cca) {
            alert('Faltan datos necesarios (pda o cca)');
            return;
        }

        // Extraer partida: últimos 6 caracteres de pda, quitando ceros a la izquierda
        const partidaRaw = pda.slice(-6);
        const partida = partidaRaw.replace(/^0+/, '') || '0';
        
        // Extraer datos de parcela desde cca (42 caracteres)
        // Últimos 7 caracteres: "000800A" -> nro=0008, letra=00A (quitando ceros)
        const ccaSuffix = cca.slice(-7); // últimos 7 caracteres
        const parcelaNum = ccaSuffix.slice(0, 4).replace(/^0+/, '') || '0'; // quitar ceros a la izquierda
        const parcelaLetra = ccaSuffix.slice(4).replace(/^0+/, ''); // quitar ceros antes de letra
        const parcelaTexto = parcelaNum + (parcelaLetra ? parcelaLetra : '');

        console.log('Importando desde popup:', { pda, partida, cca, ccaSuffix, parcelaNum, parcelaLetra, parcelaTexto });

        if (!partido) {
            alert('No se ha definido el partido');
            return;
        }

        try {
            // Llamar al endpoint de ARBA para obtener la geometría real
            const partidoNumero = parseInt(partido, 10);
            const response = await fetch(
                `/api/arba/partida?pdo=${encodeURIComponent(partidoNumero)}&pda=${encodeURIComponent(partida)}`
            );
            const data = await response.json();

            if (!data.found || !data.result) {
                alert(`Partida ${partida} no encontrada en ARBA`);
                return;
            }

            // Parsear WKT a geometría
            const wktFormat = new WKT();
            const geometry = wktFormat.readGeometry(data.result);
            
            // Transformar de EPSG:3857 a EPSG:4326
            geometry.transform('EPSG:3857', 'EPSG:4326');

            // Crear feature
            const feature = new Feature({ geometry });
            feature.set('partida', partida);
            feature.set('parcela', parcelaTexto);
            feature.set('label', parcelaTexto || `PDA ${partida}`);
            feature.set('source', 'arba-click');

            parcelasSource.current.addFeature(feature);
            notifyGeometryChange();
            
            // Cerrar popup
            setArbaFeatureInfo(null);
            
            // Zoom a la parcela
            const extent = geometry.getExtent();
            mapInstance.current?.getView().fit(extent, {
                padding: [50, 50, 50, 50],
                duration: 800,
                maxZoom: 19
            });
            
            console.log('Parcela importada exitosamente:', { partida, parcelaTexto });
        } catch (error) {
            console.error('Error importando parcela:', error);
            alert('Error al importar la parcela: ' + error.message);
        }
    }, [arbaFeatureInfo, partido, notifyGeometryChange]);

    // --- 4. EFECTO: ACTUALIZAR ESTADO DE GEOMETRÍAS ---
    useEffect(() => {
        const parcelasCount = parcelasSource.current?.getFeatures().length || 0;
        const sedeCount = sedeSource.current?.getFeatures().length || 0;
        setHasGeometries(parcelasCount > 0 || sedeCount > 0);
    }, [initialFeatures, initialFeaturesParcelas]);

    useImperativeHandle(ref, () => ({
        clearMap: () => {
            sedeSource.current.clear();
            parcelasSource.current.clear();
            notifyGeometryChange();
        }
    }));

	// --- RESALTAR GEOMETRÍA ---
	const highlightFeature = useCallback((type, index) => {
		const map = mapInstance.current;
		if (!map) return;

		// Limpiar resaltado anterior
		if (selectedFeatureRef.current) {
			selectedFeatureRef.current.set('selected', false);
			selectedFeatureRef.current = null;
		}

		let feature = null;
		if (type === 'sede') {
			const sedeFeatures = sedeSource.current.getFeatures();
			feature = sedeFeatures[0] || null;
		} else if (type === 'parcela') {
			const parcelasFeatures = parcelasSource.current.getFeatures();
			feature = parcelasFeatures[index] || null;
		}

		if (feature) {
			feature.set('selected', true);
			selectedFeatureRef.current = feature;
			
			// Centrar el mapa en la geometría
			const geometry = feature.getGeometry();
			const extent = geometry.getExtent();
			const center = geometry.getType() === 'Point' ? geometry.getCoordinates() : [
				(extent[0] + extent[2]) / 2,
				(extent[1] + extent[3]) / 2
			];
			
			map.getView().animate({
				center: center,
				duration: 500
			});
		}

		// Forzar redibujado
		if (parcelasLayerRef.current) parcelasLayerRef.current.changed();
		sedeSource.current.changed();
	}, []);

	// --- ELIMINAR GEOMETRÍA ---
	const deleteGeometry = useCallback((type, index) => {
		// Limpiar resaltado antes de eliminar
		if (selectedFeatureRef.current) {
			selectedFeatureRef.current.set('selected', false);
			selectedFeatureRef.current = null;
		}
		if (type === 'sede') {
			sedeSource.current.clear();
		} else if (type === 'parcela') {
			const features = parcelasSource.current.getFeatures();
			if (features[index]) {
				parcelasSource.current.removeFeature(features[index]);
			}
		}
		setSelectedWKTIndex(null);
		notifyGeometryChange();
		// Forzar redibujado
		if (parcelasLayerRef.current) parcelasLayerRef.current.changed();
		sedeSource.current.changed();
	}, [notifyGeometryChange]);

	const renderWKTList = () => {
		const items = [];
		
		// Agregar sede si existe
		const sedeFeatures = sedeSource.current.getFeatures();
		if (sedeFeatures.length > 0) {
			const sede = sedeFeatures[0];
			const coords = sede.getGeometry().getCoordinates();
			items.push(
				<li
					key="sede"
					className={`my-2 p-3 rounded-md border-2 transition-colors duration-200 list-none cursor-pointer flex justify-between items-center
						${selectedWKTIndex === 'sede' 
							? 'bg-indigo-100 border-indigo-500 text-indigo-800 shadow-md' 
							: 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'}`}
					onClick={() => {
						setSelectedWKTIndex('sede');
						highlightFeature('sede');
					}}
				>
					<div className="flex items-center gap-2">
						<span className="text-lg">📍</span>
						<span className="text-sm font-medium">Sede</span>
						<span className="text-xs text-gray-500 font-mono">
							{coords[1].toFixed(5)}, {coords[0].toFixed(5)}
						</span>
					</div>
					<button
						onClick={(e) => {
							e.stopPropagation();
							deleteGeometry('sede');
						}}
						className="p-1 bg-red-500 text-white rounded-md text-xs hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
					>
						🗑️
					</button>
				</li>
			);
		}
		
		// Agregar parcelas
		const parcelasFeatures = parcelasSource.current.getFeatures();
		parcelasFeatures.forEach((feature, index) => {
			// Leer datos directamente de la feature (sincronizado con el mapa)
			const partida = feature.get('partida') || '';
			const parcela = feature.get('parcela') || '';
			items.push(
				<li
					key={`parcela-${index}`}
					className={`my-2 p-3 rounded-md border-2 transition-colors duration-200 list-none cursor-pointer
						${selectedWKTIndex === `parcela-${index}` 
							? 'bg-indigo-100 border-indigo-500 text-indigo-800 shadow-md' 
							: 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'}`}
					onClick={() => {
						setSelectedWKTIndex(`parcela-${index}`);
						highlightFeature('parcela', index);
					}}
				>
					<div className="flex items-center gap-2 mb-2">
						<span className="text-lg">⬢</span>
						<span className="text-sm font-medium">Parcela {index + 1}</span>
						<button
							onClick={(e) => {
								e.stopPropagation();
								deleteGeometry('parcela', index);
							}}
							className="p-1 bg-red-500 text-white rounded-md text-xs hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
						>
							🗑️
						</button>
					</div>
					<div className="flex gap-2 items-center text-xs">
						<div className="flex items-center gap-1">
							<label className="text-gray-500">Partida:</label>
							<input
								type="text"
								maxLength="6"
								placeholder="000000"
								value={partida}
								onClick={(e) => e.stopPropagation()}
								onChange={(e) => updateParcelaData(index, 'partida', e.target.value)}
								className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs font-mono"
							/>
						</div>
						<div className="flex items-center gap-1">
							<label className="text-gray-500">Parcela:</label>
							<input
								type="text"
								maxLength="10"
								placeholder="Nro/Letra"
								value={parcela}
								onClick={(e) => e.stopPropagation()}
								onChange={(e) => updateParcelaData(index, 'parcela', e.target.value)}
								className="w-20 px-1 py-0.5 border border-gray-300 rounded text-xs font-mono"
							/>
						</div>
					</div>
				</li>
			);
		});
		
		return items;
	};

    return (
        <div className="h-full w-full relative group">
            <div ref={mapRef} className="h-full w-full bg-slate-100" />

            {/* Contenedor del Zoom - posicionado arriba a la derecha */}
            <div 
                ref={zoomRef}
                className="absolute top-4 right-4 z-10"
            />

            {/* CSS para mover el control de zoom de OpenLayers */}
            <style>{`
                .ol-zoom {
                    position: absolute !important;
                    top: 16px !important;
                    right: 16px !important;
                    left: auto !important;
                    z-index: 10 !important;
                }
            `}</style>

            {/* Menú Acordeón - Lado Izquierdo */}
            <div className="absolute top-4 left-4 flex flex-col gap-2 z-10 w-64">
                {/* Sección Herramientas */}
                <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                    <button
                        onClick={() => setAccordionTools(!accordionTools)}
                        className="w-full px-4 py-3 flex items-center justify-between font-semibold text-sm text-white bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 transition-all"
                    >
                        <span className="flex items-center gap-2">
                            <ToolsIcon />
                            Herramientas
                        </span>
                        <span className={`transform transition-transform ${accordionTools ? 'rotate-180' : ''}`}>▼</span>
                    </button>
                    {accordionTools && (
                        <div className="p-2 space-y-1">
                            {!sinSede && (
                                <ToolButton active={activeTool === 'point'} onClick={() => startDrawing('point')} icon="📍" label="Sede" />
                            )}
                            <ToolButton active={activeTool === 'polygon'} onClick={() => startDrawing('polygon')} icon="⬢" label="Parcela" />
                            <div className="h-px bg-slate-100 my-2" />
                            <ToolButton active={isEditingMode} onClick={toggleEditMode} icon={isEditingMode ? "🔒" : "✏️"} label={isEditingMode ? "Guardar" : "Editar"} color={isEditingMode ? "text-orange-600" : "text-purple-600"} />
                            <ToolButton active={showArbaForm} onClick={() => setShowArbaForm(true)} icon="🏛️" label="Importar Partidas" color="text-blue-600" />
                            {hasGeometries && (
                                <>
                                    <div className="h-px bg-slate-100 my-2" />
                                    <ToolButton active={false} onClick={zoomToGeometries} icon="�" label="Ver Geometrías" color="text-emerald-600" />
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Sección Capas */}
                <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                    <button
                        onClick={() => setAccordionLayers(!accordionLayers)}
                        className="w-full px-4 py-3 flex items-center justify-between font-semibold text-sm text-white bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 transition-all"
                    >
                        <span className="flex items-center gap-2">
                            <LayersIcon />
                            Capas
                        </span>
                        <span className={`transform transition-transform ${accordionLayers ? 'rotate-180' : ''}`}>▼</span>
                    </button>
                    {accordionLayers && (
                        <div className="p-2 space-y-2">
                            {/* Capa ARBA con herramienta de consulta al lado */}
                            <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                                <div className="flex items-center gap-2">
                                    <ToolButton active={showArbaLayer} onClick={() => setShowArbaLayer(!showArbaLayer)} icon="🗺️" label={showArbaLayer ? "Ocultar CartoARBA" : "Ver CartoARBA"} color="text-green-600" />
                                    {showArbaLayer && (
                                        <ToolButton active={arbaInfoMode} onClick={() => setArbaInfoMode(!arbaInfoMode)} icon="ℹ️" label="Info" color={arbaInfoMode ? "text-cyan-600" : "text-gray-500"} />
                                    )}
                                </div>
                            </div>
                            {/* Aquí se agregarán más capas base */}
                            <div className="border border-gray-200 rounded-lg p-2 bg-gray-50 mt-2">
                                <h5 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Mapa Base</h5>
                                <div className="space-y-1">
                                    <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                        <input 
                                            type="radio" 
                                            name="baseLayer" 
                                            value="osm" 
                                            checked={baseLayer === 'osm'}
                                            onChange={() => setBaseLayer('osm')}
                                            className="text-blue-600"
                                        />
                                        <span className="text-sm text-gray-700">🗺️ OpenStreetMap</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                        <input 
                                            type="radio" 
                                            name="baseLayer" 
                                            value="ign" 
                                            checked={baseLayer === 'ign'}
                                            onChange={() => setBaseLayer('ign')}
                                            className="text-blue-600"
                                        />
                                        <span className="text-sm text-gray-700">🇦🇷 IGN Argentina</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                        <input 
                                            type="radio" 
                                            name="baseLayer" 
                                            value="positron" 
                                            checked={baseLayer === 'positron'}
                                            onChange={() => setBaseLayer('positron')}
                                            className="text-blue-600"
                                        />
                                        <span className="text-sm text-gray-700">⚪ CartoDB Positron</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                        <input 
                                            type="radio" 
                                            name="baseLayer" 
                                            value="voyager" 
                                            checked={baseLayer === 'voyager'}
                                            onChange={() => setBaseLayer('voyager')}
                                            className="text-blue-600"
                                        />
                                        <span className="text-sm text-gray-700">🏢 CartoDB Voyager (edificios)</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                        <input 
                                            type="radio" 
                                            name="baseLayer" 
                                            value="satellite" 
                                            checked={baseLayer === 'satellite'}
                                            onChange={() => setBaseLayer('satellite')}
                                            className="text-blue-600"
                                        />
                                        <span className="text-sm text-gray-700">🛰️ Esri Satellite</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                        <input 
                                            type="radio" 
                                            name="baseLayer" 
                                            value="dark" 
                                            checked={baseLayer === 'dark'}
                                            onChange={() => setBaseLayer('dark')}
                                            className="text-blue-600"
                                        />
                                        <span className="text-sm text-gray-700">⚫ CartoDB Dark</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

			{/* Indicador de Modo Dibujo */ }
			{
				activeTool && activeTool !== 'edit' && (
					<div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-slate-900/80 text-white px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase animate-pulse">
						Dibujando {activeTool === 'point' ? 'Sede' : 'Parcela'}... [Esc] para cancelar
					</div>
				)
			}

			{/* Indicador de Modo Edición */ }
			{
				isEditingMode && (
					<div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-orange-600/90 text-white px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase">
						✏️ Arrastrar vértices | Se acercan automáticamente | Alt+Click elimina
					</div>
				)
			}

			{/* Formulario ARBA */}
			{showArbaForm && (
				<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl border border-gray-200 p-6 w-80 z-50">
					<div className="flex justify-between items-center mb-4">
						<h3 className="font-bold text-lg text-gray-800">Importar desde ARBA</h3>
						<button 
							onClick={() => setShowArbaForm(false)}
							className="text-gray-400 hover:text-gray-600 text-xl"
						>
							×
						</button>
					</div>
					
					<div className="mb-4">
						<p className="text-sm text-gray-600 mb-2">
							Partido: <span className="font-mono font-semibold">{partido || 'No definido'}</span>
						</p>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Números de Partida
						</label>
						<textarea
							value={arbaPartidas}
							onChange={(e) => setArbaPartidas(e.target.value)}
							placeholder="Ej: 66894, 66895, 66900-66910"
							className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
							rows={3}
							disabled={isLoadingArba}
						/>
						<p className="text-xs text-gray-500 mt-1">
							Separar por comas (,) o guiones (-) para rangos
						</p>
					</div>

					<div className="flex gap-2">
						<button
							onClick={() => setShowArbaForm(false)}
							disabled={isLoadingArba}
							className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
						>
							Cancelar
						</button>
						<button
							onClick={importFromArba}
							disabled={isLoadingArba || !partido}
							className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex justify-center items-center gap-2"
						>
							{isLoadingArba ? (
								<>
									<span className="animate-spin">⟳</span>
									Importando...
								</>
							) : (
								'Importar'
							)}
						</button>
					</div>
				</div>
			)}

			{/* Popup GetFeatureInfo ARBA */}
			{arbaError && (
				<div className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-xl z-50 max-w-sm text-sm flex items-center gap-2">
					<span>⚠️</span>
					<span>{arbaError}</span>
					<button onClick={() => setArbaError(null)} className="ml-auto text-red-400 hover:text-red-600">×</button>
				</div>
			)}

			{arbaLoadingInfo && (
				<div className="absolute inset-0 bg-black/10 z-40 flex items-center justify-center pointer-events-auto" style={{ cursor: 'wait' }}>
					<div className="bg-white rounded-xl shadow-2xl px-6 py-4 flex items-center gap-3">
						<svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
							<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
						</svg>
						<span className="text-sm font-medium text-gray-700">Consultando ARBA...</span>
					</div>
				</div>
			)}

			{arbaFeatureInfo && arbaInfoPosition && showArbaLayer && arbaInfoMode && (
				<div 
					className="absolute bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 max-w-md"
					style={{
						left: arbaInfoPosition.x + 10,
						top: arbaInfoPosition.y - 10
					}}
				>
					<div className="flex justify-between items-start mb-2">
						<h4 className="font-bold text-sm text-gray-800">📋 Información Parcela</h4>
						<button
							onClick={() => setArbaFeatureInfo(null)}
							className="text-gray-400 hover:text-gray-600 text-lg leading-none"
						>
							×
						</button>
					</div>
					<div className="text-xs space-y-1 mb-3 max-h-40 overflow-y-auto">
						{Object.entries(arbaFeatureInfo.properties || arbaFeatureInfo).map(([key, value]) => (
							<div key={key} className="flex justify-between gap-2">
								<span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
								<span className="font-medium text-gray-800 break-all" title={String(value)}>{String(value)}</span>
							</div>
						))}
					</div>
					{arbaFeatureInfo.properties?.pda && (
                        <button
                            onClick={() => {
                                console.log('Botón Importar clickeado, llamando a importArbaParcelFromPopup');
                                importArbaParcelFromPopup();
                            }}
                            className="w-full px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded hover:bg-blue-700 transition-colors"
                        >
                            ⬇️ Importar esta parcela
                        </button>
                    )}
				</div>
			)}

			{(parcelasSource.current.getFeatures().length > 0 || sedeSource.current.getFeatures().length > 0) && (
				<div className="absolute bottom-2 right-2 z-10">
					{isMinimized ? (
						// Estado minimizado - solo header
						<div 
							onClick={toggleMinimize}
							className="px-4 py-3 cursor-pointer transition-all select-none text-white flex items-center justify-between bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500"
						>
							<div className="flex items-center gap-2">
								<DrawingsIcon />
								<span className="font-semibold text-sm">Geometrías Dibujadas</span>
								<span className="text-xs px-2 py-0.5 rounded-full bg-white/30">
									{parcelasSource.current.getFeatures().length + sedeSource.current.getFeatures().length}
								</span>
							</div>
							<span className={`transform transition-transform text-xs opacity-75`}>▼</span>
						</div>
					) : (
						// Estado maximizado - lista completa
						<div className="w-72 bg-white rounded-xl shadow-xl overflow-hidden border border-slate-200">
							<div 
								onClick={toggleMinimize}
								className="px-4 py-3 cursor-pointer transition-all select-none text-white flex items-center justify-between bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500"
							>
								<div className="flex items-center gap-2">
									<DrawingsIcon />
									<span className="font-semibold text-sm">Geometrías Dibujadas</span>
									<span className="text-xs px-2 py-0.5 rounded-full bg-white/30">
										{parcelasSource.current.getFeatures().length + sedeSource.current.getFeatures().length}
									</span>
								</div>
								<span className={`transform transition-transform text-xs opacity-75`}>▲</span>
							</div>
							<div className="max-h-80 overflow-y-auto bg-gray-50">
								<ul className="divide-y divide-gray-200">
									{renderWKTList()}
								</ul>
							</div>
						</div>
					)}
				</div>
			)}
        </div>		
    );
});

const ToolButton = ({ active, onClick, icon, label, color = "text-slate-600" }) => (
    <button onClick={onClick} className={`flex items-center gap-3 px-4 py-2 rounded-xl transition-all ${active ? 'bg-blue-600 text-white' : `hover:bg-slate-50 ${color}`}`}>
        <span className="text-lg">{icon}</span>
        <span className="text-[10px] font-bold uppercase">{label}</span>
    </button>
);

export default MapViewMapeo;